import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers/params";
import getOrderByQuery from "/app/src/helpers/table";
import { ColumnSort } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";

type PaginatedService<TItem, TParams = URLSearchParams> = {
  getAll: (params?: TParams) => Promise<{
    [key: string]: TItem[];
  }>;
  getCount: (params?: TParams) => Promise<{
    count: number;
  }>;
};

/**
 * A hook to manage and fetch paginated data using a given service.
 *
 * @param {Object} params - The parameters for fetching paginated data.
 * @param {string[]} params.queryKey - Array of strings to uniquely identify the query.
 * @param {string} [params.searchString=""] - Optional search string for filtering results.
 * @param {number} [params.page] - The current page number for pagination.
 * @param {number} [params.pageSize] - The number of items per page.
 * @param {ColumnSort[]} [params.sort] - Array of sorting options.
 * @param {PaginatedService<T, URLSearchParams>} params.service - The service to fetch data from.
 * @param {Record<string, unknown>} [params.options={}] - Additional options for the query.
 * @param {number | false} [params.refetchInterval=false] - Interval for refetching data automatically.
 *
 * @returns {Object} An object containing paginated data, count, fetching status, a refetch function, and query keys.
 */
export default function usePaginatedData<T>({
  queryKey,
  searchString = "",
  sort,
  service,
  options = {},
  refetchInterval = false,
  page,
  pageSize,
  enabled = true,
}: {
  queryKey: string[];
  searchString?: string;
  sort?: ColumnSort[];
  service: PaginatedService<T, URLSearchParams>;
  options?: Record<string, unknown>;
  refetchInterval?: number | false;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const countQueryKey = useMemo(
    () => [...queryKey, "count", searchString, options],
    [queryKey, searchString, options],
  );
  const shouldRefetchCountOnMount =
    queryClient.getQueryData(countQueryKey) === undefined;

  const { refetch: refetchData, ...queryInfo } = useQuery({
    queryKey: [...queryKey, searchString, page, pageSize, sort, options],
    queryFn: () =>
      service.getAll(
        buildParams({
          limit: pageSize,
          page,
          search: searchString,
          orderby: getOrderByQuery(sort),
          ...options,
        }),
      ),
    placeholderData: { [queryKey[0]]: [] },
    select: (response: { [key: string]: T[] }) => response[queryKey[0]],
    refetchInterval,
    enabled,
    refetchOnMount:
      queryClient.getQueryData([
        ...queryKey,
        searchString,
        page,
        pageSize,
        sort,
        options,
      ]) === undefined,
  });

  const { data: countData, refetch: refetchCount } = useQuery({
    queryKey: countQueryKey,
    queryFn: () =>
      service.getCount(
        buildParams({
          search: searchString,
          ...options,
        }),
      ),
    placeholderData: { count: 0 },
    select: (response: { count: number }) => response.count,
    refetchInterval,
    enabled,
    refetchOnMount: shouldRefetchCountOnMount,
  });

  // Ensure queries run when navigating directly via URL (no cache yet).
  useEffect(() => {
    if (!enabled) return;

    const dataQueryKey = [
      ...queryKey,
      searchString,
      page,
      pageSize,
      sort,
      options,
    ];
    const countQueryKeyLocal = countQueryKey;

    // If cache missing, trigger a fetch for data and count explicitly.
    if (queryClient.getQueryData(dataQueryKey) === undefined) {
      queryClient
        .fetchQuery(dataQueryKey, () =>
          service.getAll(
            buildParams({
              limit: pageSize,
              page,
              search: searchString,
              orderby: getOrderByQuery(sort),
              ...options,
            }),
          ),
        )
        .catch(() => undefined);
    }

    if (queryClient.getQueryData(countQueryKeyLocal) === undefined) {
      queryClient
        .fetchQuery(countQueryKeyLocal, () =>
          service.getCount(
            buildParams({
              search: searchString,
              ...options,
            }),
          ),
        )
        .catch(() => undefined);
    }
  }, [
    enabled,
    queryClient,
    queryKey,
    searchString,
    page,
    pageSize,
    sort,
    options,
    service,
    countQueryKey,
  ]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    refetchData();
    refetchCount();
  }, [enabled, refetchData, refetchCount]);

  const getQueryKeys = useCallback(() => {
    return {
      dataQueryKey: [...queryKey, searchString, page, pageSize, sort, options],
      countQueryKey: [...queryKey, "count", searchString, options],
    };
  }, [queryKey, searchString, page, pageSize, sort, options]);

  return {
    count: countData,
    refetch,
    getQueryKeys,
    ...queryInfo,
  };
}
